import React, { createContext, useState, useEffect, useMemo, useCallback } from 'react';
import {
  getAllOrganizations,
  getFacilitiesByOrganization,
  updateOrganization,
  addOrganization,
  addAddress,
  updateAddress,
  getAllAddresses,
  addFacility,
  updateFacility,
  getAllAddressesByFacId,
  getAllFacilities,
} from '../services/apiServices';
import { useFeedbackContext } from './FeedbackContext';
import { ACTION_TYPES } from '../constants/actionTypes';
import { serviceLogger } from '../utils/serviceLogger';

export const OrganizationFacilityContext = createContext();

export const OrganizationFacilityProvider = ({ children }) => {
  const { showModal, showToast } = useFeedbackContext();

  // Organization States
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [activeOrganizations, setActiveOrganizations] = useState([]);
  const [inactiveOrganizations, setInactiveOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('Active');
  const [organizationSearchTerm, setOrganizationSearchTerm] = useState('');
  const [addressDetails, setAddressDetails] = useState({
    street: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
  });
  const [isEditingOrg, setIsEditingOrg] = useState(false);
  const [showOrganizationModal, setShowOrganizationModal] = useState(false);
  const [organizationModalAction, setOrganizationModalAction] = useState('');
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(true);
  const [isLoadingAddOrganization, setIsLoadingAddOrganization] = useState(false);
  const [isLoadingEditOrganization, setIsLoadingEditOrganization] = useState(false);
  const [isLoadingToggleStatus, setIsLoadingToggleStatus] = useState(false);

  // Facility States
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [FacSelectedOrganization, setFacSelectedOrganization] = useState(null);
  const [facilitySearchTerm, setFacilitySearchTerm] = useState('');
  const [FacOrganizationSearchTerm, setFacOrganizationSearchTerm] = useState('');
  const [facilityAddressDetails, setFacilityAddressDetails] = useState({
    street: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
  });
  const [isEditingFacility, setIsEditingFacility] = useState(false);
  const [showFacilityModal, setShowFacilityModal] = useState(false);
  const [facilityModalAction, setFacilityModalAction] = useState('');
  const [allFacilities, setAllFacilities] = useState([]);
  const [isLoadingFacilities, setIsLoadingFacilities] = useState(false);
  const [isLoadingAddFacility, setIsLoadingAddFacility] = useState(false);
  const [isLoadingEditFacility, setIsLoadingEditFacility] = useState(false);
  const [isLoadingToggleFacilityStatus, setIsLoadingToggleFacilityStatus] = useState(false);

  // Effects
  // Load organizations and facilities on component mount
  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoadingOrganizations(true);
        const orgs = await getAllOrganizations();
        const facilitiesData = await getAllFacilities();

        setAllOrganizations(orgs);
        setActiveOrganizations(orgs.filter((org) => org.is_active));
        setInactiveOrganizations(orgs.filter((org) => !org.is_active));

        setAllFacilities(facilitiesData);
      } catch (error) {
        serviceLogger.error('Error fetching organizations or facilities:', error);
      } finally {
        setIsLoadingOrganizations(false);
      }
    }
    fetchData();
  }, []);

  // Organization Functions
  const getFilteredOrganizations = useCallback(() => {
    return allOrganizations.filter((org) =>
      org.name.toLowerCase().includes(organizationSearchTerm.toLowerCase()),
    );
  }, [allOrganizations, organizationSearchTerm]);

  const getFilteredOrganizationFacilities = useCallback(() => {
    return facilities.filter((facility) =>
      facility.name.toLowerCase().includes(FacOrganizationSearchTerm.toLowerCase()),
    );
  }, [facilities, FacOrganizationSearchTerm]);

  const handleSelectStatus = useCallback((option) => {
    setSelectedStatus(option);
    setSelectedOrganization(null);
  }, []);

  const handleOrganizationSearch = useCallback((term) => {
    setOrganizationSearchTerm(term);
  }, []);

  const handleAddOrganization = useCallback(async () => {
    setIsLoadingAddOrganization(true);
    try {
      setIsEditingOrg(false);
      setOrganizationModalAction(ACTION_TYPES.ADD);
      setShowOrganizationModal(true);
    } finally {
      setIsLoadingAddOrganization(false);
    }
  }, []);

  const handleEditOrganization = useCallback(async () => {
    if (!selectedOrganization) {
      showToast({
        variant: 'warning',
        title: 'No Organization Selected',
        copy: 'Please choose an organization before editing.',
      });
      return;
    }
    setIsLoadingEditOrganization(true);
    try {
      setIsEditingOrg(true);
      setOrganizationModalAction(ACTION_TYPES.EDIT);
      setShowOrganizationModal(true);
    } catch (error) {
      serviceLogger.error('Error fetching organization address:', error);
    } finally {
      setIsLoadingEditOrganization(false);
    }
  }, [selectedOrganization, showToast]);

  const confirmToggleOrganizationStatus = useCallback(
    async (organizationId, currentStatus, action) => {
      try {
        const updatedOrganization = await updateOrganization(organizationId, {
          is_active: !currentStatus,
        });
        const updatedOrganizations = allOrganizations.map((org) =>
          org.id === organizationId ? { ...updatedOrganization } : org,
        );

        setAllOrganizations(updatedOrganizations);
        setActiveOrganizations(updatedOrganizations.filter((org) => org.is_active));
        setInactiveOrganizations(updatedOrganizations.filter((org) => !org.is_active));

        if (selectedOrganization?.id === organizationId) {
          setSelectedOrganization(updatedOrganization);
        }

        showToast({
          variant: 'success',
          title: 'Success',
          copy: `Organization ${action}d successfully!`,
        });
      } catch (error) {
        serviceLogger.error('Error toggling organization:', error);
        showToast({
          variant: 'error',
          title: 'Error',
          copy: `We could not ${action} this organization. Please try again.`,
        });
      } finally {
        setIsLoadingToggleStatus(false);
      }
    },
    [
      allOrganizations,
      selectedOrganization,
      setAllOrganizations,
      setActiveOrganizations,
      setInactiveOrganizations,
      setSelectedOrganization,
      showToast,
      setIsLoadingToggleStatus,
    ],
  );

  const handleToggleOrganizationStatus = useCallback(
    (organizationId, currentStatus) => {
      setIsLoadingToggleStatus(true);
      const action = currentStatus ? 'disable' : 'enable';

      showModal({
        variant: 'warning',
        title: `Are you sure you want to ${action} this organization?`,
        copy: 'This change will update the organization status immediately.',
        buttonMessage: 'Confirm',
        onClickButton: () => confirmToggleOrganizationStatus(organizationId, currentStatus, action),
      });
    },
    [showModal, confirmToggleOrganizationStatus, setIsLoadingToggleStatus],
  );

  const handleSelectOrganization = useCallback(
    async (option) => {
      const selectedOrg = allOrganizations.find((org) => org.id === option.value);
      setSelectedOrganization(selectedOrg);
      setOrganizationSearchTerm('');

      if (selectedOrg) {
        try {
          const addresses = await getAllAddresses(selectedOrg.id);
          setAddressDetails(addresses.length > 0 ? addresses[0] : {});
        } catch (error) {
          serviceLogger.error('Error fetching addresses:', error);
        }
      }
    },
    [allOrganizations],
  );

  const handleSaveOrganization = useCallback(
    async (organizationDetails) => {
      try {
        if (!organizationDetails.name) {
          return;
        }

        if (organizationModalAction === ACTION_TYPES.ADD) {
          const newOrganization = await addOrganization(organizationDetails);

          const updatedOrganizations = [...allOrganizations, newOrganization];

          setAllOrganizations(updatedOrganizations);
          setActiveOrganizations(updatedOrganizations.filter((org) => org.is_active));
          setInactiveOrganizations(updatedOrganizations.filter((org) => !org.is_active));
        } else if (organizationModalAction === ACTION_TYPES.EDIT) {
          await updateOrganization(selectedOrganization.id, organizationDetails);

          const updatedOrganizations = allOrganizations.map((org) =>
            org.id === selectedOrganization.id ? { ...org, ...organizationDetails } : org,
          );

          setAllOrganizations(updatedOrganizations);
          setActiveOrganizations(updatedOrganizations.filter((org) => org.is_active));
          setInactiveOrganizations(updatedOrganizations.filter((org) => !org.is_active));
          setSelectedOrganization({ ...selectedOrganization, ...organizationDetails });

          if (FacSelectedOrganization?.id === selectedOrganization.id) {
            setFacSelectedOrganization({ ...FacSelectedOrganization, ...organizationDetails });
            setSelectedFacility(null);
          }
        }
        setShowOrganizationModal(false);
        showToast({
          variant: 'success',
          title: 'Success',
          copy: 'Organization saved successfully!',
        });
      } catch (error) {
        serviceLogger.error('Error saving organization:', error);
        showToast({
          variant: 'error',
          title: 'Save failed',
          copy: 'We could not save your changes. Please try again.',
        });
      }
    },
    [
      organizationModalAction,
      allOrganizations,
      selectedOrganization,
      FacSelectedOrganization,
      setSelectedFacility,
      showToast,
    ],
  );

  // Facility Functions
  const handleFacOrganizationSearch = useCallback((term) => {
    setFacOrganizationSearchTerm(term);
  }, []);

  const handleFacSelectOrganization = useCallback(
    async (option) => {
      const selectedOrg = allOrganizations.find((org) => org.id === option.id);
      setFacSelectedOrganization(selectedOrg);
      setFacOrganizationSearchTerm('');
      setSelectedFacility(null);

      if (selectedOrg) {
        try {
          setIsLoadingFacilities(true);
          const facilitiesForOrg = await getFacilitiesByOrganization(selectedOrg.id);
          setFacilities(facilitiesForOrg);
        } catch (error) {
          serviceLogger.error(`Error fetching facilities for organization ${selectedOrg.id}:`, error);
          setFacilities([]);
        } finally {
          setIsLoadingFacilities(false);
        }
      } else {
        setFacilities([]);
        setIsLoadingFacilities(false);
      }
    },
    [allOrganizations],
  );

  const handleFacilitySearch = useCallback((term) => {
    setFacilitySearchTerm(term);
  }, []);

  const handleSelectFacility = useCallback(
    async (option) => {
      const selectedFac = facilities.find((fac) => fac.id === option.value);
      setSelectedFacility(selectedFac);

      if (selectedFac) {
        try {
          const address = await getAllAddressesByFacId(selectedFac.id);
          setFacilityAddressDetails(address.length > 0 ? address[0] : {});
        } catch (error) {
          serviceLogger.error('Error fetching address for facility:', error);
        }
      }

      setIsEditingFacility(true);
    },
    [facilities],
  );

  const handleAddFacility = useCallback(() => {
    if (!FacSelectedOrganization) {
      showToast({
        variant: 'warning',
        title: 'No Organization Selected',
        copy: 'Please choose an organization before adding a facility.',
      });
      return;
    }
    setIsLoadingAddFacility(true);
    try {
      setIsEditingFacility(false);
      setFacilityModalAction(ACTION_TYPES.ADD);
      setShowFacilityModal(true);
    } finally {
      setIsLoadingAddFacility(false);
    }
  }, [FacSelectedOrganization, showToast]);

  const handleEditFacility = useCallback(async () => {
    if (!selectedFacility) {
      showToast({
        variant: 'warning',
        title: 'No Facility Selected',
        copy: 'Please choose a facility before editing.',
      });
      return;
    }
    setIsLoadingEditFacility(true);
    try {
      const address = await getAllAddressesByFacId(selectedFacility.id);

      setFacilityAddressDetails(address.length > 0 ? address[0] : {});
      setIsEditingFacility(true);
      setFacilityModalAction(ACTION_TYPES.EDIT);
      setShowFacilityModal(true);
    } catch (error) {
      serviceLogger.error('Error fetching facility address:', error);
    } finally {
      setIsLoadingEditFacility(false);
    }
  }, [selectedFacility, showToast]);

  const confirmToggleFacilityStatus = useCallback(
    async (facilityId, currentStatus, action) => {
      try {
        const updatedFacility = await updateFacility(facilityId, { is_active: !currentStatus });

        const updatedFacilities = facilities.map((facility) =>
          facility.id === facilityId ? { ...facility, is_active: !currentStatus } : facility,
        );
        setFacilities(updatedFacilities);

        if (selectedFacility?.id === facilityId) {
          setSelectedFacility(updatedFacility);
        }

        showToast({
          variant: 'success',
          title: 'Success',
          copy: `Facility ${action}d successfully!`,
        });
      } catch (error) {
        serviceLogger.error('Error toggling facility status:', error);
        showToast({
          variant: 'error',
          title: 'Error',
          copy: `We could not ${action} this facility. Please try again.`,
        });
      } finally {
        setIsLoadingToggleFacilityStatus(false);
      }
    },
    [
      facilities,
      selectedFacility,
      showToast,
      setFacilities,
      setSelectedFacility,
      setIsLoadingToggleFacilityStatus,
    ],
  );

  const handleToggleFacilityStatus = useCallback(
    (facilityId, currentStatus) => {
      setIsLoadingToggleFacilityStatus(true);
      const action = currentStatus ? 'disable' : 'enable';

      showModal({
        variant: 'warning',
        title: `Are you sure you want to ${action} this facility?`,
        copy: 'This change will update the facility status immediately.',
        buttonMessage: 'Confirm',
        onClickButton: () => confirmToggleFacilityStatus(facilityId, currentStatus, action),
      });
    },
    [showModal, confirmToggleFacilityStatus, setIsLoadingToggleFacilityStatus],
  );

  const handleSaveFacility = useCallback(
    async (facilityDetails, addressDetails) => {
      try {
        if (!facilityDetails.name || !facilityDetails.type) {
          return;
        }

        let newFacility = null;

        if (facilityModalAction === ACTION_TYPES.ADD) {
          newFacility = await addFacility({
            ...facilityDetails,
            //address_details: [addressDetails],
            organization: FacSelectedOrganization.id,
          });

          setFacilities((prevFacilities) => [...prevFacilities, newFacility]);
          setSelectedFacility(newFacility);
          showToast({
            variant: 'success',
            title: 'Success',
            copy: 'Facility created successfully!',
          });
        } else if (facilityModalAction === ACTION_TYPES.EDIT) {
          await updateFacility(selectedFacility.id, facilityDetails);

          const updatedFacilities = facilities.map((fac) =>
            fac.id === selectedFacility.id ? { ...fac, ...facilityDetails } : fac,
          );
          setFacilities(updatedFacilities);
          setSelectedFacility({ ...selectedFacility, ...facilityDetails });
          showToast({
            variant: 'success',
            title: 'Success',
            copy: 'Facility updated successfully!',
          });
        }

        if (addressDetails) {
          try {
            if (facilityModalAction === ACTION_TYPES.EDIT) {
              if (selectedFacility.address_id) {
                await updateAddress(selectedFacility.address_id, addressDetails);
              } else {
                await addAddress({
                  ...addressDetails,
                  facility: selectedFacility.id,
                });
              }
            } else if (facilityModalAction === ACTION_TYPES.ADD && newFacility) {
              await addAddress({
                ...addressDetails,
                facility: newFacility.id,
              });
            }
          } catch (error) {
            serviceLogger.error('Error handling address:', error);
            showToast({
              variant: 'warning',
              title: 'Failed to update address',
              copy: `Facility was ${facilityModalAction.toLowerCase()}ed, but we could not update the address.`,
            });
          }
        }

        setShowFacilityModal(false);
        return facilityModalAction === ACTION_TYPES.ADD ? newFacility : selectedFacility;
      } catch (error) {
        serviceLogger.error('Error in handleSaveFacility:', error);
        showToast({
          variant: 'error',
          title: 'Failed to create or update facility',
          copy: 'We could not complete the action. Please try again.',
        });
      }
    },
    [facilityModalAction, FacSelectedOrganization, facilities, selectedFacility, showToast],
  );

  // Context Value
  const value = useMemo(
    () => ({
      // States and functions for organizations
      organizationState: {
        allOrganizations,
        activeOrganizations,
        inactiveOrganizations,
        selectedOrganization,
        selectedStatus,
        organizationSearchTerm,
        addressDetails,
        isEditingOrg,
        showOrganizationModal,
        organizationModalAction,
        isLoadingOrganizations,
        isLoadingAddOrganization,
        isLoadingEditOrganization,
        isLoadingToggleStatus,
      },
      organizationActions: {
        setSelectedOrganization,
        setSelectedStatus,
        setShowOrganizationModal,
        getFilteredOrganizations,
        handleSelectOrganization,
        handleSelectStatus,
        handleOrganizationSearch,
        handleAddOrganization,
        handleEditOrganization,
        handleToggleOrganizationStatus,
        handleSaveOrganization,
      },
      // States and functions for facilities
      facilityState: {
        facilities,
        allFacilities,
        selectedFacility,
        FacSelectedOrganization,
        facilitySearchTerm,
        FacOrganizationSearchTerm,
        facilityAddressDetails,
        isEditingFacility,
        showFacilityModal,
        facilityModalAction,
        isLoadingFacilities,
        isLoadingAddFacility,
        isLoadingEditFacility,
        isLoadingToggleFacilityStatus,
      },
      facilityActions: {
        setSelectedFacility,
        setFacSelectedOrganization,
        setShowFacilityModal,
        handleFacSelectOrganization,
        handleFacOrganizationSearch,
        handleFacilitySearch,
        handleSelectFacility,
        handleAddFacility,
        handleEditFacility,
        handleSaveFacility,
        handleToggleFacilityStatus,
        getFilteredOrganizationFacilities,
      },
    }),
    [
      // Dependencies
      allOrganizations,
      activeOrganizations,
      inactiveOrganizations,
      selectedOrganization,
      selectedStatus,
      organizationSearchTerm,
      addressDetails,
      isEditingOrg,
      showOrganizationModal,
      organizationModalAction,
      facilities,
      allFacilities,
      selectedFacility,
      FacSelectedOrganization,
      facilitySearchTerm,
      FacOrganizationSearchTerm,
      facilityAddressDetails,
      isEditingFacility,
      showFacilityModal,
      facilityModalAction,
      isLoadingOrganizations,
      isLoadingFacilities,
      isLoadingAddOrganization,
      isLoadingEditOrganization,
      isLoadingToggleStatus,
      isLoadingAddFacility,
      isLoadingEditFacility,
      isLoadingToggleFacilityStatus,
      // Actions
      setSelectedOrganization,
      setSelectedStatus,
      setShowOrganizationModal,
      getFilteredOrganizations,
      handleSelectOrganization,
      handleSelectStatus,
      handleOrganizationSearch,
      handleAddOrganization,
      handleEditOrganization,
      handleToggleOrganizationStatus,
      handleSaveOrganization,
      setSelectedFacility,
      setFacSelectedOrganization,
      setShowFacilityModal,
      handleFacSelectOrganization,
      handleFacOrganizationSearch,
      handleFacilitySearch,
      handleSelectFacility,
      handleAddFacility,
      handleEditFacility,
      handleSaveFacility,
      handleToggleFacilityStatus,
      getFilteredOrganizationFacilities,
    ],
  );

  return (
    <OrganizationFacilityContext.Provider value={value}>
      {children}
    </OrganizationFacilityContext.Provider>
  );
};
